import { ApplicationInsights, ITelemetryPlugin } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { createBrowserHistory, createMemoryHistory, History } from "history";

const react_plugin = new ReactPlugin();
let app_insights: ApplicationInsights | null = null;
let is_initialized = false;

// INITIALIZATION & SETUP

const initializeAppInsights = (history: History) => {
    app_insights = new ApplicationInsights({
        config: {
            // NOTE: "CLIENT" placeholder must match the one used in backend telemetry proxy
            connectionString: "InstrumentationKey=CLIENT;IngestionEndpoint=/api/telemetry", // data goes to our backend
            disableInstrumentationKeyValidation: true,
            enableCorsCorrelation: true,
            enableRequestHeaderTracking: true,
            enableResponseHeaderTracking: true,
            enableAutoRouteTracking: true,
            distributedTracingMode: 2, // W3C distributed tracing mode
            disableExceptionTracking: false,
            disableAjaxTracking: false,
            disableFetchTracking: false,
            enableUnhandledPromiseRejectionTracking: true,
            autoTrackPageVisitTime: true,
            extensions: [react_plugin as unknown as ITelemetryPlugin], // workaround for type incompatibility due to react version mismatch
            extensionConfig: {
                [react_plugin.identifier]: {
                    history: history
                }
            }
        }
    });

    // filter out telemetry data older than 40 hours
    app_insights.addTelemetryInitializer((envelope: any) => {
        if (envelope.time && Date.now() - new Date(envelope.time).getTime() > 40 * 60 * 60 * 1000) {
            return false; // drop stale data
        }
        return true;
    });

    app_insights.loadAppInsights();
    console.info("Telemetry initialized successfully");
};

export const initializeTelemetry = (history_type: "browser" | "memory" = "browser") => {
    if (is_initialized) {
        return;
    }

    // create appropriate history
    const app_history = history_type === "memory"
        ? createMemoryHistory()
        : createBrowserHistory();

    // initialize Application Insights
    initializeAppInsights(app_history);

    is_initialized = true;

    // set up automatic console.error logging
    setupConsoleErrorLogging();
};

export const setupConsoleErrorLogging = () => {
    // store original console.error
    const original_console_error = console.error;

    // override console.error to also log to Application Insights
    console.error = (...args: any[]) => {
        // call original console.error
        original_console_error(...args);

        // log to telemetry if initialized
        if (is_initialized && app_insights && args.length > 0) {
            const error_message = args.map(arg =>
                typeof arg === "string" ? arg : JSON.stringify(arg)
            ).join(" ");

            app_insights.trackException({
                exception: new Error(error_message),
                properties: {
                    source: "console.error",
                    timestamp: new Date().toISOString()
                }
            });
        }
    };
};

// TRACKING

export const trackTelemetryEvent = (name: string, properties?: { [key: string]: any }) => {
    if (is_initialized && app_insights) {
        app_insights.trackEvent({ name, properties });
    }
};

export const trackTelemetryException = (exception: Error, properties?: { [key: string]: any }) => {
    if (is_initialized && app_insights) {
        app_insights.trackException({ exception, properties });
    }
};

export const trackTelemetryPageView = (name?: string, uri?: string, properties?: { [key: string]: any }) => {
    if (is_initialized && app_insights) {
        app_insights.trackPageView({ name, uri, properties });
    }
};

export const trackTelemetryMetric = (name: string, value: number, properties?: { [key: string]: any }) => {
    if (is_initialized && app_insights) {
        app_insights.trackMetric({ name, average: value, properties });
    }
};

// CONTEXT

export const setTelemetryUser = (user_id: string) => {
    if (is_initialized && app_insights) {
        app_insights.setAuthenticatedUserContext(user_id);
    }
};

export const clearTelemetryUser = () => {
    if (is_initialized && app_insights) {
        app_insights.clearAuthenticatedUserContext();
    }
};

export const getTelemetryTraceContext = () => {
    if (is_initialized && app_insights) {
        return app_insights.context?.telemetryTrace;
    }
    return null;
};

// AUXILIARY

export const generateSpanId = (): string => {
    const bytes = new Uint8Array(8);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte: number) => byte.toString(16).padStart(2, "0")).join("");
};

export { react_plugin };